/**
 * Raise the size cap baked into an image URL.
 *
 * The image service takes a base64 payload of edits after the file path. The
 * AI wall's URLs carry a single `resize` capped at the width the wall needed,
 * which is why asking the CDN for a wider render gives back the capped one —
 * the cap is in the payload, not the request. Rewriting that width serves the
 * same frame at a higher resolution: same edits, same crop, so the picture
 * sharpens without re-framing.
 *
 * Anything that is not a lone `resize` — a crop, a zoom, a signed payload — is
 * left alone and the original URL comes back, so an unfamiliar shape degrades
 * to the image the caller already had.
 */
export const resizeImageUrl = (imageUrl: string, width: number): string => {
    const cut = imageUrl.lastIndexOf('/');
    if (cut < 0) return imageUrl;
    const payload = imageUrl.slice(cut + 1);
    // Base64url with the padding stripped, as the service writes it.
    if (!/^[A-Za-z0-9+/_-]{16,}={0,2}$/.test(payload)) return imageUrl;

    try {
        const decoded = JSON.parse(
            atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
        ) as { edits?: { type?: string; options?: { width?: number } }[] };
        const edits = decoded.edits;
        const only = edits?.length === 1 ? edits[0] : undefined;
        if (
            !only ||
            only.type !== 'resize' ||
            typeof only.options?.width !== 'number' ||
            only.options.width >= width
        ) {
            return imageUrl;
        }

        const raised = {
            ...decoded,
            edits: [{ ...only, options: { ...only.options, width } }],
        };
        // The CDN prefix caps the render too, so it is raised alongside the
        // payload — otherwise the wider render is just downscaled again.
        const path = imageUrl
            .slice(0, cut)
            .replace(/(width(?:%3D|=))\d+/i, `$1${width}`);
        return `${path}/${btoa(JSON.stringify(raised)).replace(/=+$/, '')}`;
    } catch {
        return imageUrl;
    }
};
